import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { TrackerService } from 'src/app/shared/util/tracker.service';
import { environment } from 'src/environments/environment';

export interface FaqItem {
  questionKey: string;
  answerKey: string;
}

export interface HelpResourceLink {
  icon: string;
  titleKey: string;
  textKey: string;
  route: string;
}

/** Sticky header (~4rem) + small gap */
const SCROLL_HEADER_OFFSET = 80;

@Component({
  selector: 'app-myimmo-help',
  templateUrl: './myimmo-help.component.html',
  styleUrls: ['./myimmo-help.component.scss'],
})
export class MyImmoHelpComponent implements OnInit, AfterViewInit, OnDestroy {
  assistantHref = '';
  openFaqIndex: number | null = 0;

  private routerEventsSub: Subscription | null = null;
  private fragmentScrollTimer: ReturnType<typeof setTimeout> | null = null;

  readonly faqItems: FaqItem[] = [
    { questionKey: 'HOME.FEATURE_QUESTION1_TITLE', answerKey: 'HOME.FEATURE_QUESTION1_TEXT' },
    { questionKey: 'HOME.FEATURE_QUESTION2_TITLE', answerKey: 'HOME.FEATURE_QUESTION2_TEXT' },
    { questionKey: 'HOME.FEATURE_QUESTION3_TITLE', answerKey: 'HOME.FEATURE_QUESTION3_TEXT' },
    { questionKey: 'HOME.FEATURE_QUESTION4_TITLE', answerKey: 'HOME.FEATURE_QUESTION4_TEXT' },
    { questionKey: 'HOME.FEATURE_QUESTION5_TITLE', answerKey: 'HOME.FEATURE_QUESTION5_TEXT' },
  ];

  readonly resourceLinks: HelpResourceLink[] = [
    {
      icon: 'lni-calculator',
      titleKey: 'HOME.CALCULATORS_TITLE',
      textKey: 'HOME.CALCULATORS_TEXT_SHORT',
      route: '/calculators',
    },
    {
      icon: 'lni-files',
      titleKey: 'HOME.DOCUMENTS_AND_TEMPLATES_TITLE',
      textKey: 'HOME.DOCUMENTS_AND_TEMPLATES_TEXT_SHORT',
      route: '/documents',
    },
    {
      icon: 'lni-book',
      titleKey: 'HOME.FISCAL_GUIDE_TITLE',
      textKey: 'HOME.FISCAL_GUIDE_TEXT_SHORT',
      route: '/fiscal-guide',
    },
    {
      icon: 'lni-dashboard',
      titleKey: 'HOME.MANAGEMENT_TOOL_TITLE',
      textKey: 'HOME.MANAGEMENT_TOOL_TEXT_SHORT',
      route: '/management-tool',
    },
    {
      icon: 'lni-checkbox',
      titleKey: 'HOME.TODO_LISTS_TITLE',
      textKey: 'HOME.TODO_LISTS_TEXT_SHORT',
      route: '/todo',
    },
  ];

  constructor(
    public translate: TranslateService,
    private trackerService: TrackerService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.trackerService.trackPageViews();
    this.assistantHref = environment.assistantApp;

    this.routerEventsSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        const fragment = this.getUrlFragment();
        if (fragment) {
          this.scheduleScrollToFragment(fragment);
        }
      });

    const initialFragment = this.getUrlFragment();
    if (initialFragment) {
      this.scheduleScrollToFragment(initialFragment);
    }
  }

  ngAfterViewInit(): void {
    const fragment = this.getUrlFragment();
    if (fragment) {
      this.scheduleScrollToFragment(fragment);
    }
  }

  ngOnDestroy(): void {
    this.routerEventsSub?.unsubscribe();
    if (this.fragmentScrollTimer) {
      clearTimeout(this.fragmentScrollTimer);
    }
  }

  toggleFaq(index: number): void {
    this.openFaqIndex = this.openFaqIndex === index ? null : index;
  }

  private getUrlFragment(): string | null {
    return this.router.parseUrl(this.router.url).fragment;
  }

  private scheduleScrollToFragment(fragment: string): void {
    if (this.fragmentScrollTimer) {
      clearTimeout(this.fragmentScrollTimer);
    }
    this.fragmentScrollTimer = setTimeout(() => this.scrollToFragment(fragment, 0), 50);
  }

  private scrollToFragment(fragment: string, attempt: number): void {
    const target = document.getElementById(fragment);
    if (target) {
      const top =
        target.getBoundingClientRect().top + window.pageYOffset - SCROLL_HEADER_OFFSET;
      window.scrollTo({
        top: Math.max(0, top),
        behavior: attempt === 0 ? 'smooth' : 'auto',
      });
      return;
    }

    if (attempt < 20) {
      setTimeout(() => this.scrollToFragment(fragment, attempt + 1), 100);
    }
  }
}
